import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GraphQLTimesheetService } from '../../services/graphqlTimesheet';
import { Project, Task, ProjectStatus, ProjectMember, CreateProjectInput, CreateTaskInput } from '../../types/timesheet';
import { loadAllUsers } from '../../services/userLoader';
import RightSidePanel from '../performance/RightSidePanel';
import InlineLoader from '../common/InlineLoader';
import EmptyState from '../common/EmptyState';
import './ProjectsManager.css';

interface SimpleUser { id: string; email: string; name: string; department: string; }

type FilterKey = 'All' | ProjectStatus;
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'All', label: 'All' },
  { key: ProjectStatus.ACTIVE, label: 'Active' },
  { key: ProjectStatus.COMPLETED, label: 'Completed' },
  { key: ProjectStatus.ON_HOLD, label: 'On Hold' },
];

const ProjectsManager: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filter, setFilter] = useState<FilterKey>('All');
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [allUsers, setAllUsers] = useState<SimpleUser[]>([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [addingMember, setAddingMember] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const p = await GraphQLTimesheetService.listProjects();
      setProjects(p.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    } catch (err) { console.error('[ProjectsManager] Load error:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    loadAllUsers().then(u => setAllUsers(u.map(x => ({ id: x.id, email: x.email, name: x.name, department: x.department })))).catch(() => {});
  }, []);

  const handleSelectProject = async (project: Project) => {
    if (selectedProject?.id === project.id) {
      // Toggle collapse
      setSelectedProject(null);
      return;
    }
    setSelectedProject(project);
    setShowTaskForm(false);
    setShowMemberForm(false);
    const [t, m] = await Promise.all([
      GraphQLTimesheetService.getTasksByProject(project.id),
      GraphQLTimesheetService.getProjectMembers(project.id),
    ]);
    setTasks(t);
    setMembers(m);
  };

  const handleCloseDetail = () => {
    setSelectedProject(null);
    setShowTaskForm(false);
    setShowMemberForm(false);
  };

  const handleCreateProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData(e.currentTarget);
      const input: CreateProjectInput = {
        name: fd.get('name') as string,
        code: fd.get('code') as string,
        clientName: fd.get('clientName') as string,
        startDate: fd.get('startDate') as string,
        endDate: fd.get('endDate') as string,
        billable: fd.get('billable') === 'true',
      };
      await GraphQLTimesheetService.createProject(input);
      setShowCreatePanel(false);
      showToast('Project created successfully', 'success');
      await loadProjects();
    } catch (err) { showToast('Failed to create project.', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleEditProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingProject) return;
    setSubmitting(true);
    try {
      const fd = new FormData(e.currentTarget);
      const updates = {
        name: fd.get('name') as string,
        code: fd.get('code') as string,
        clientName: fd.get('clientName') as string,
        startDate: fd.get('startDate') as string,
        endDate: fd.get('endDate') as string,
        billable: fd.get('billable') === 'true',
      };
      await GraphQLTimesheetService.updateProject(editingProject.id, updates as any);
      setShowEditPanel(false);
      setEditingProject(null);
      showToast('Project updated successfully', 'success');
      await loadProjects();
      if (selectedProject?.id === editingProject.id) {
        setSelectedProject({ ...selectedProject, ...updates });
      }
    } catch (err) { showToast('Failed to update project.', 'error'); }
    finally { setSubmitting(false); }
  };

  const openEditPanel = (project: Project) => {
    setEditingProject(project);
    setShowEditPanel(true);
  };

  const handleStatusChange = async (project: Project, newStatus: ProjectStatus) => {
    if (statusUpdating) return;
    const confirmMsg = newStatus === ProjectStatus.ON_HOLD
      ? `Put "${project.name}" on hold? Team members won't be able to log time against it.`
      : newStatus === ProjectStatus.COMPLETED
      ? `Mark "${project.name}" as completed?`
      : `Reactivate "${project.name}"?`;

    setConfirmModal({
      message: confirmMsg,
      onConfirm: async () => {
        setConfirmModal(null);
        setStatusUpdating(true);
        try {
          const updated = await GraphQLTimesheetService.updateProject(project.id, { status: newStatus } as any);
          if (updated) {
            showToast(`Project status changed to ${newStatus}`, 'success');
            await loadProjects();
            if (selectedProject?.id === project.id) {
              setSelectedProject({ ...project, status: newStatus });
            }
          } else {
            showToast('Failed to update status.', 'error');
          }
        } catch (err) {
          console.error('[ProjectsManager] Status update error:', err);
          showToast('Failed to update status.', 'error');
        } finally {
          setStatusUpdating(false);
        }
      }
    });
  };

  const handleAddTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedProject) return;
    const fd = new FormData(e.currentTarget);
    try {
      await GraphQLTimesheetService.createTask({
        projectId: selectedProject.id,
        name: fd.get('taskName') as string,
        description: fd.get('taskDescription') as string || '',
        billable: fd.get('taskBillable') === 'true',
      });
      setShowTaskForm(false);
      showToast('Task added', 'success');
      const t = await GraphQLTimesheetService.getTasksByProject(selectedProject.id);
      setTasks(t);
    } catch { showToast('Failed to add task.', 'error'); }
  };

  const handleAddMember = async (employeeEmail: string) => {
    if (!selectedProject) return;
    const u = allUsers.find(x => x.email === employeeEmail);
    if (!u) return;
    setAddingMember(employeeEmail);
    try {
      await GraphQLTimesheetService.addProjectMember(selectedProject.id, u.email, u.name);
      const m = await GraphQLTimesheetService.getProjectMembers(selectedProject.id);
      setMembers(m);
      setMemberSearch('');
      showToast(`${u.name} added to project`, 'success');
    } catch { showToast('Failed to add member.', 'error'); }
    finally { setAddingMember(null); }
  };

  const handleRemoveMember = async (employeeId: string) => {
    if (!selectedProject) return;
    setConfirmModal({
      message: 'Remove this member from the project?',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await GraphQLTimesheetService.removeProjectMember(selectedProject.id, employeeId);
          setMembers(prev => prev.filter(m => m.employeeId !== employeeId));
          showToast('Member removed', 'success');
        } catch { showToast('Failed to remove member.', 'error'); }
      }
    });
  };

  const filtered = filter === 'All' ? projects : projects.filter(p => p.status === filter);
  const counts: Record<string, number> = { All: projects.length };
  projects.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });

  const memberEmails = new Set(members.map(m => m.employeeId));
  const filteredUsers = allUsers.filter(u =>
    !memberEmails.has(u.email) &&
    (u.name.toLowerCase().includes(memberSearch.toLowerCase()) || u.email.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  return (
    <section className="tms-projects-page">
      <div className="tms-projects-header">
        <h2 className="tms-projects-title">Projects & Tasks</h2>
        <button className="tms-btn tms-btn-primary" onClick={() => setShowCreatePanel(true)}>+ New Project</button>
      </div>

      <div className="tms-projects-filters">
        {FILTERS.map(f => (
          <button key={f.key} className={`tms-projects-filter ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label} <span className="tms-projects-filter-badge">{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="tms-projects-layout">
        {/* Project List */}
        <div className="tms-projects-list">
          {loading ? <InlineLoader message="Loading projects" /> : filtered.length === 0 ? (
            <EmptyState icon="rocket" title="No projects yet" subtitle="Create your first project to get started." actionLabel="+ New Project" onAction={() => setShowCreatePanel(true)} />
          ) : (
            filtered.map(p => (
              <div key={p.id} className={`tms-project-card ${selectedProject?.id === p.id ? 'selected' : ''}`} onClick={() => handleSelectProject(p)}>
                <div className="tms-project-card__header">
                  <div>
                    <span className="tms-project-card__name">{p.name}</span>
                    <span className="tms-project-card__code">{p.code}</span>
                  </div>
                  <div className="tms-project-card__header-actions">
                    <button className="tms-btn-sm tms-btn-sm-blue" onClick={(e) => { e.stopPropagation(); openEditPanel(p); }} title="Edit project">✎</button>
                    <span className={`tms-status-badge tms-project-status-${p.status.toLowerCase().replace(' ', '-')}`}>{p.status}</span>
                  </div>
                </div>
                <div className="tms-project-card__meta">
                  <span>Client: {p.clientName}</span>
                  <span>{p.startDate} — {p.endDate}</span>
                  {p.billable && <span className="tms-project-card__billable">💰 Billable</span>}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Project Detail */}
        {selectedProject ? (
          <div className="tms-project-detail">
            <div className="tms-project-detail__header">
              <h3>{selectedProject.name} <span className="tms-project-card__code">{selectedProject.code}</span></h3>
              <div className="tms-project-detail__header-right">
                <button className="tms-btn-sm tms-btn-sm-blue" onClick={() => openEditPanel(selectedProject)}>✎ Edit</button>
                <div className="tms-project-detail__status-actions">
                  {selectedProject.status !== ProjectStatus.ACTIVE && (
                    <button className="tms-btn-sm tms-btn-sm-green" onClick={() => handleStatusChange(selectedProject, ProjectStatus.ACTIVE)} disabled={statusUpdating}>
                      {statusUpdating ? '...' : 'Set Active'}
                    </button>
                  )}
                  {selectedProject.status !== ProjectStatus.ON_HOLD && (
                    <button className="tms-btn-sm tms-btn-sm-yellow" onClick={() => handleStatusChange(selectedProject, ProjectStatus.ON_HOLD)} disabled={statusUpdating}>
                      {statusUpdating ? '...' : 'On Hold'}
                    </button>
                  )}
                  {selectedProject.status !== ProjectStatus.COMPLETED && (
                    <button className="tms-btn-sm tms-btn-sm-gray" onClick={() => handleStatusChange(selectedProject, ProjectStatus.COMPLETED)} disabled={statusUpdating}>
                      {statusUpdating ? '...' : 'Complete'}
                    </button>
                  )}
                </div>
                <button className="tms-project-detail__close" onClick={handleCloseDetail} title="Close">✕</button>
              </div>
            </div>

            {/* On Hold Banner */}
            {selectedProject.status === ProjectStatus.ON_HOLD && (
              <div className="tms-onhold-banner">⏸️ This project is on hold. Team members cannot log time against it.</div>
            )}

            {/* Tasks */}
            <div className="tms-project-detail__section">
              <div className="tms-project-detail__section-header">
                <h4>Tasks ({tasks.length})</h4>
                <button className="tms-btn-sm tms-btn-sm-green" onClick={() => setShowTaskForm(!showTaskForm)}>
                  {showTaskForm ? '✕ Cancel' : '+ Add Task'}
                </button>
              </div>
              {showTaskForm && (
                <form className="tms-inline-form" onSubmit={handleAddTask}>
                  <input name="taskName" placeholder="Task name *" required />
                  <input name="taskDescription" placeholder="Description" />
                  <select name="taskBillable"><option value="true">Billable</option><option value="false">Non-billable</option></select>
                  <button type="submit" className="tms-btn-sm tms-btn-sm-green">Save</button>
                </form>
              )}
              {tasks.length === 0 ? <p className="tms-project-detail__empty">No tasks. Add one above.</p> : (
                <div className="tms-task-list">
                  {tasks.map(t => (
                    <div key={t.id} className="tms-task-item">
                      <span className="tms-task-item__name">{t.name}</span>
                      {t.description && <span className="tms-task-item__desc">{t.description}</span>}
                      {t.billable && <span className="tms-task-item__billable">💰</span>}
                      {!t.isActive && <span className="tms-task-item__inactive">Inactive</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Members */}
            <div className="tms-project-detail__section">
              <div className="tms-project-detail__section-header">
                <h4>Team Members ({members.length})</h4>
                <button className="tms-btn-sm tms-btn-sm-green" onClick={() => { setShowMemberForm(!showMemberForm); setMemberSearch(''); }}>
                  {showMemberForm ? '✕ Cancel' : '+ Add Member'}
                </button>
              </div>
              {showMemberForm && (
                <div className="tms-member-search">
                  <input placeholder="Search employees..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)} autoFocus />
                  <div className="tms-member-search__results">
                    {filteredUsers.slice(0, 8).map(u => (
                      <button
                        key={u.email}
                        className={`tms-member-search__item ${addingMember === u.email ? 'adding' : ''}`}
                        onClick={() => handleAddMember(u.email)}
                        disabled={addingMember !== null}
                      >
                        <span>{u.name}</span>
                        <span className="tms-member-search__email">
                          {addingMember === u.email ? 'Adding...' : u.email}
                        </span>
                      </button>
                    ))}
                    {memberSearch && filteredUsers.length === 0 && (
                      <p className="tms-member-search__no-results">No matching employees found</p>
                    )}
                  </div>
                </div>
              )}
              {members.length === 0 ? <p className="tms-project-detail__empty">No members assigned.</p> : (
                <div className="tms-member-list">
                  {members.map(m => (
                    <div key={m.employeeId} className="tms-member-item">
                      <span className="tms-member-item__name">{m.employeeName}</span>
                      <span className="tms-member-item__email">{m.employeeId}</span>
                      <button className="tms-member-item__remove" onClick={() => handleRemoveMember(m.employeeId)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="tms-project-detail tms-project-detail--empty">
            <p>Select a project to view details</p>
          </div>
        )}
      </div>

      {/* Create Project Panel */}
      <RightSidePanel isOpen={showCreatePanel} title="New Project" onClose={() => setShowCreatePanel(false)} wide>
        <form className="tms-entry-form" onSubmit={handleCreateProject}>
          <div className="tms-entry-form__field"><label>Project Name *</label><input name="name" required placeholder="e.g. CreyenteOne EMS" /></div>
          <div className="tms-entry-form__row">
            <div className="tms-entry-form__field"><label>Project Code *</label><input name="code" required placeholder="e.g. CO-EMS" /></div>
            <div className="tms-entry-form__field"><label>Client Name *</label><input name="clientName" required placeholder="e.g. Creyente InfoTech" /></div>
          </div>
          <div className="tms-entry-form__row">
            <div className="tms-entry-form__field"><label>Start Date *</label><input name="startDate" type="date" required /></div>
            <div className="tms-entry-form__field"><label>End Date *</label><input name="endDate" type="date" required /></div>
          </div>
          <div className="tms-entry-form__field">
            <label>Billable</label>
            <select name="billable"><option value="true">Yes — Billable</option><option value="false">No — Non-billable</option></select>
          </div>
          <div className="tms-entry-form__actions">
            <button type="button" className="tms-btn tms-btn-outline" onClick={() => setShowCreatePanel(false)}>Cancel</button>
            <button type="submit" className="tms-btn tms-btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Project'}</button>
          </div>
        </form>
      </RightSidePanel>

      {/* Edit Project Panel */}
      <RightSidePanel isOpen={showEditPanel} title="Edit Project" onClose={() => { setShowEditPanel(false); setEditingProject(null); }} wide>
        {editingProject && (
          <form className="tms-entry-form" onSubmit={handleEditProject}>
            <div className="tms-entry-form__field"><label>Project Name *</label><input name="name" required defaultValue={editingProject.name} /></div>
            <div className="tms-entry-form__row">
              <div className="tms-entry-form__field"><label>Project Code *</label><input name="code" required defaultValue={editingProject.code} /></div>
              <div className="tms-entry-form__field"><label>Client Name *</label><input name="clientName" required defaultValue={editingProject.clientName} /></div>
            </div>
            <div className="tms-entry-form__row">
              <div className="tms-entry-form__field"><label>Start Date *</label><input name="startDate" type="date" required defaultValue={editingProject.startDate} /></div>
              <div className="tms-entry-form__field"><label>End Date *</label><input name="endDate" type="date" required defaultValue={editingProject.endDate} /></div>
            </div>
            <div className="tms-entry-form__field">
              <label>Billable</label>
              <select name="billable" defaultValue={editingProject.billable ? 'true' : 'false'}><option value="true">Yes — Billable</option><option value="false">No — Non-billable</option></select>
            </div>
            <div className="tms-entry-form__actions">
              <button type="button" className="tms-btn tms-btn-outline" onClick={() => { setShowEditPanel(false); setEditingProject(null); }}>Cancel</button>
              <button type="submit" className="tms-btn tms-btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        )}
      </RightSidePanel>

      {/* Toast */}
      {toast && <div className={`tms-toast tms-toast-${toast.type}`} role="alert">{toast.message}</div>}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="tms-confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="tms-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="tms-confirm-modal__icon">⚙️</div>
            <h3 className="tms-confirm-modal__title">Confirm Action</h3>
            <p className="tms-confirm-modal__message">{confirmModal.message}</p>
            <div className="tms-confirm-modal__actions">
              <button className="tms-btn tms-btn-outline" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button className="tms-btn tms-btn-primary" onClick={confirmModal.onConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectsManager;
